import { useAudioLevel } from '../../hooks/useAudioLevel'

interface AudioMeterProps {
  stream: MediaStream | null
  size?: 'sm' | 'md'
  bars?: number
  /**
   * Optional pre-computed level (0..100). When provided, overrides the
   * stream-based detection. Used for the local participant where we read
   * LiveKit's own audioLevel — the MediaStream-from-publication path is
   * unreliable for self because publish/unpublish cycles thrash the
   * underlying source node.
   */
  audioLevel?: number
}

export default function AudioMeter({ stream, size = 'sm', bars = 4, audioLevel }: AudioMeterProps) {
  const detectedLevel = useAudioLevel(audioLevel === undefined ? stream : null)
  const level = audioLevel === undefined ? detectedLevel : audioLevel

  const heightClass = size === 'sm' ? 'h-3' : 'h-5'
  const barWidth = size === 'sm' ? 'w-[3px]' : 'w-[4px]'
  const gap = size === 'sm' ? 'gap-[2px]' : 'gap-1'

  // More sensitive thresholds for speech
  const thresholds = [2, 6, 15, 30, 50, 75].slice(0, bars)

  return (
    <div className={`flex items-end ${gap} ${heightClass}`} title={`Mic level: ${level}%`}>
      {thresholds.map((threshold, i) => {
        const filled = level >= threshold
        const isPeak = i === bars - 1 && level > 85
        return (
          <div
            key={i}
            className={`${barWidth} rounded-full transition-all duration-75 ${
              filled
                ? isPeak
                  ? 'bg-destructive'
                  : 'bg-green-500'
                // Unfilled bars: pick a theme-aware shade. The name tag has
                // a black/60 backdrop, so we keep the original light tint so
                // bars stay visible. Removed the white/20-only logic which
                // disappeared on light backgrounds when this meter is used
                // outside the name tag.
                : 'bg-white/40'
            }`}
            style={{
              height: `${((i + 1) / bars) * 100}%`,
              opacity: filled ? 1 : 0.6,
            }}
          />
        )
      })}
    </div>
  )
}
